/**
 * Admin Dashboard Page
 *
 * Displays aggregate platform metrics and time-series charts.
 */

import { useState } from "react";
import {
  Pulse as Activity,
  ArrowsClockwise,
  Cpu,
  CurrencyDollar,
  FileText,
  Fire as Flame,
  Books as Library,
  TrendUp as TrendingUp,
  Trophy,
  Users,
  Warning,
  XLogo,
  Lightning as Zap,
} from "@phosphor-icons/react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Alert,
  HeaderAction,
  PageHeader,
  SegmentedControl,
  Spinner,
  StatTile,
  type StatTone,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@charisol-spark/ui";
import { useAdminDashboard, useAdminMetrics } from "@/hooks/useAdmin";
import { Link } from "react-router-dom";
import { hasRole } from "@/components/auth/AdminGuard";
import {
  COST_NOT_REPORTED,
  type MetricsResponse,
  type TopStreaker,
} from "@/services/admin";
import { formatMicroUsd } from "@/utils/formatting";

// ── Stat Card ──────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ComponentProps<typeof StatTile>["icon"];
  tone?: StatTone;
  /** When set, the whole card links into that metric's detail page. */
  to?: string;
}

function StatCard({ label, value, icon, tone, to }: StatCardProps) {
  return (
    <StatTile
      label={label}
      value={typeof value === "number" ? value.toLocaleString() : value}
      icon={icon}
      tone={tone}
      {...(to ? { component: Link, componentProps: { to } } : {})}
    />
  );
}

/** Placeholder shown per-card while the dashboard's first fetch is in flight. */
function StatCardSkeleton() {
  return (
    <div
      className="animate-pulse rounded-xl border border-border/50 bg-card p-4 sm:p-5"
      aria-hidden="true"
    >
      <div className="h-10 w-10 rounded-xl bg-muted" />
      <div className="mt-3 h-3 w-20 rounded bg-muted" />
      <div className="mt-2 h-6 w-16 rounded bg-muted" />
    </div>
  );
}

// ── Chart Section ──────────────────────────────────────────

const METRICS = [
  { key: "users", label: "Users" },
  { key: "sources", label: "Sources" },
  { key: "drafts", label: "Drafts" },
  { key: "generations", label: "AI Generations" },
  { key: "cost", label: "Provider Cost" },
] as const;

const PERIODS = [
  { key: "day", label: "Daily" },
  { key: "week", label: "Weekly" },
  { key: "month", label: "Monthly" },
] as const;

/** A chart value in the unit the API reported it in. */
function formatMetricValue(
  value: number,
  unit: MetricsResponse["unit"] | undefined
): string {
  return unit === "microUsd" ? formatMicroUsd(value) : value.toLocaleString();
}

function MetricsChart() {
  const [metric, setMetric] = useState<string>("users");
  const [period, setPeriod] = useState<string>("month");

  const { data, isLoading } = useAdminMetrics(period, metric);

  return (
    <div className="rounded-xl border border-border/50 bg-card p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-base font-semibold">Growth Trends</h2>
        <div className="flex flex-wrap gap-2">
          <SegmentedControl
            label="Metric"
            value={metric}
            onChange={setMetric}
            options={METRICS.map((m) => ({ value: m.key, label: m.label }))}
          />
          <SegmentedControl
            label="Period"
            value={period}
            onChange={setPeriod}
            options={PERIODS.map((p) => ({ value: p.key, label: p.label }))}
          />
        </div>
      </div>

      {data?.note && (
        <p className="-mt-2 mb-3 text-xs text-muted-foreground">{data.note}</p>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart
            data={data?.data ?? []}
            margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--border))"
              opacity={0.5}
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              stroke="hsl(var(--muted-foreground))"
              tickFormatter={(v: string) => {
                if (period === "month") return v.slice(5); // MM
                return v.slice(5); // MM-DD
              }}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              stroke="hsl(var(--muted-foreground))"
              allowDecimals={false}
              width={data?.unit === "microUsd" ? 72 : 60}
              tickFormatter={(v: number) => formatMetricValue(v, data?.unit)}
            />
            <Tooltip
              formatter={(v) => [
                formatMetricValue(Number(v), data?.unit),
                METRICS.find((m) => m.key === metric)?.label ?? metric,
              ]}
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                fontSize: "12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              fill="url(#areaGradient)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── Top Streakers Table ────────────────────────────────────

function TopStreakersTable({ streakers }: { streakers: TopStreaker[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
      <div className="flex items-center gap-2 px-6 pb-3 pt-5">
        <Trophy className="h-5 w-5 text-amber-500" />
        <h2 className="text-base font-semibold">Top Streakers</h2>
      </div>
      <Table>
        <THead>
          <tr>
            <TH>#</TH>
            <TH>User</TH>
            <TH className="text-center">
              <Flame className="inline h-3.5 w-3.5 text-orange-500" /> Weekly
            </TH>
            <TH className="text-center">
              <Zap className="inline h-3.5 w-3.5 text-yellow-500" /> Daily
            </TH>
            <TH className="text-center">Level</TH>
            <TH className="text-right">XP</TH>
            <TH className="text-center">Badges</TH>
          </tr>
        </THead>
        <TBody>
          {streakers.map((s, i) => (
            <TR key={s.userId}>
              <TD className="tabular-nums text-muted-foreground">{i + 1}</TD>
              <TD>
                <Link
                  to={`/users/${s.userId}`}
                  className="font-medium hover:text-primary hover:underline"
                >
                  {s.name}
                </Link>
                <p className="max-w-[220px] truncate text-xs text-muted-foreground">
                  {s.email}
                </p>
              </TD>
              <TD className="text-center font-semibold tabular-nums">
                {s.currentWeeklyStreak}
              </TD>
              <TD className="text-center font-semibold tabular-nums">
                {s.currentDailyStreak}
              </TD>
              <TD className="text-center tabular-nums">{s.level}</TD>
              <TD className="text-right tabular-nums">
                {s.xp.toLocaleString()}
              </TD>
              <TD className="text-center tabular-nums">{s.badgeCount}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────

/** How many stat cards the loaded page shows — see the skeleton grid below. */
const STAT_CARD_COUNT = 8;

const PAGE_TITLE = "Platform Overview";
const PAGE_DESCRIPTION = "Real-time metrics across all users and content.";

export function AdminDashboardPage() {
  const {
    data: metrics,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useAdminDashboard();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
        <div
          className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4"
          aria-busy="true"
          aria-label="Loading dashboard metrics"
        >
          {Array.from({ length: STAT_CARD_COUNT }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="space-y-6">
        <PageHeader title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
        <Alert variant="error">
          Failed to load dashboard metrics. Please try again.
        </Alert>
      </div>
    );
  }

  // Absent from an API older than cost tracking; see COST_NOT_REPORTED.
  const cost = metrics.costThisMonth as
    | typeof metrics.costThisMonth
    | undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        action={
          <HeaderAction
            icon={<ArrowsClockwise className="h-4 w-4" />}
            onClick={() => void refetch()}
            loading={isFetching}
          >
            Refresh
          </HeaderAction>
        }
      />

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label="Total Users"
          value={metrics.totalUsers}
          icon={Users}
          tone="blue"
          to={
            hasRole("platform_admin", "platform_support") ? "/users" : undefined
          }
        />
        <StatCard
          label="Active (24h)"
          value={metrics.activeUsers24h}
          icon={Activity}
          tone="emerald"
        />
        <StatCard
          label="Sources"
          value={metrics.totalSources}
          icon={Library}
          tone="amber"
          to={
            hasRole("platform_admin", "platform_support")
              ? "/content?type=source"
              : undefined
          }
        />
        <StatCard
          label="Drafts"
          value={metrics.totalDrafts}
          icon={FileText}
          tone="violet"
          to={
            hasRole("platform_admin", "platform_support")
              ? "/content?type=draft"
              : undefined
          }
        />
        <StatCard
          label="AI Daily Avg"
          value={metrics.aiGenerationsDailyAvg}
          icon={Cpu}
          tone="cyan"
          to={
            hasRole("platform_admin", "platform_analyst")
              ? "/ai-usage"
              : undefined
          }
        />
        <StatCard
          label="AI This Month"
          value={metrics.aiGenerationsThisMonth}
          icon={TrendingUp}
          tone="amber"
          to={
            hasRole("platform_admin", "platform_analyst")
              ? "/ai-usage"
              : undefined
          }
        />
        <StatCard
          label="AI Cost This Month"
          value={cost ? formatMicroUsd(cost.aiMicroUsd) : "—"}
          icon={CurrencyDollar}
          tone="emerald"
          to={
            hasRole("platform_admin", "platform_analyst")
              ? "/ai-usage"
              : undefined
          }
        />
        <StatCard
          label="X Cost This Month"
          value={cost ? formatMicroUsd(cost.xMicroUsd) : "—"}
          icon={XLogo}
          tone="neutral"
          to={
            hasRole("platform_admin", "platform_analyst")
              ? "/ai-usage"
              : undefined
          }
        />
      </div>

      {!cost && (
        <p className="text-xs text-muted-foreground">{COST_NOT_REPORTED}</p>
      )}

      {cost && (cost.unpricedCalls > 0 || cost.fallbackCalls > 0) && (
        <p
          role="status"
          className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300"
        >
          <Warning className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          This month&apos;s cost is not exact: some calls have no listed rate.
          {hasRole("platform_admin", "platform_analyst") && (
            <Link
              to="/ai-usage"
              className="font-medium underline underline-offset-2"
            >
              See details
            </Link>
          )}
        </p>
      )}

      {/* Charts — only for roles with metrics access */}
      {hasRole("platform_admin", "platform_analyst") && <MetricsChart />}

      {/* ── Streak Metrics ── */}
      {metrics.streakMetrics && (
        <>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              Streaks & Gamification
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              User engagement through streaks and rewards.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <StatCard
              label="Weekly Streakers"
              value={metrics.streakMetrics.usersWithWeeklyStreak}
              icon={Flame}
              tone="amber"
            />
            <StatCard
              label="Daily Streakers"
              value={metrics.streakMetrics.usersWithDailyStreak}
              icon={Zap}
              tone="amber"
            />
            <StatCard
              label="Avg Weekly Streak"
              value={metrics.streakMetrics.avgWeeklyStreak}
              icon={Flame}
              tone="rose"
            />
            <StatCard
              label="Avg Daily Streak"
              value={metrics.streakMetrics.avgDailyStreak}
              icon={Zap}
              tone="rose"
            />
          </div>

          {/* Top Streakers */}
          {metrics.streakMetrics.topStreakers.length > 0 && (
            <TopStreakersTable streakers={metrics.streakMetrics.topStreakers} />
          )}
        </>
      )}
    </div>
  );
}
